import {
  FluidComponentDefinition,
  FluidEditableCellConfig,
  FluidRowData,
  FluidTableHeader,
} from '@lmig/fluid-core';
import { h } from '@stencil/core';

import { FluidTable } from '../fluid-table';
import { cellDisabledByCache } from '../internal/fluid-table-cache.utils';
import { getMutatedRowData } from '../internal/fluid-table-data-management.utils';
import {
  addCellReference,
  getEditableCellConfig,
  getMutatedValue,
  handleCellUpdate,
} from '../internal/fluid-table-editable-cell-management.utils';
import {
  handleCellClick,
  isGroupedAndShouldHideData,
} from '../internal/fluid-table-structure.utils';
import { getComponentDefinitionForCell } from '../internal/fluid-table-templating.utils';
import {
  CellTemplate,
  ComputedCellValue,
  DefaultCell,
  EditableCell,
  RenderedCell,
} from './fluid-table-cell-types.components';
import { TableActionCell } from './fluid-table-structure.components';

/**
 * Renders a table cell, depending on the cell type
 * @param props
 * @constructor
 */
export const TableCell = (props: {
  host: FluidTable;
  header: FluidTableHeader;
  dataItem: FluidRowData;
  groupHeader: boolean;
  disabledRow: boolean;
  isSubsetCell?: boolean;
  identifier: string;
}) => {
  const {
    header,
    dataItem,
    host,
    groupHeader,
    disabledRow,
    isSubsetCell,
    identifier,
  } = props;

  if (isGroupedAndShouldHideData(header)) {
    return '';
  }

  let cellDefinition;

  const editableCellConfig: FluidEditableCellConfig | undefined =
    getEditableCellConfig(header, dataItem);
  const componentDefinition: FluidComponentDefinition | undefined =
    getComponentDefinitionForCell(header, dataItem, host);

  switch (true) {
    case !!props.header.computedValueFn:
      cellDefinition = (
        <ComputedCellValue
          header={header}
          dataItem={getMutatedRowData(host._updatedRows, dataItem)}
          externalState={host.externalState}
        />
      );
      break;
    case groupHeader:
      cellDefinition = <DefaultCell dataItem={dataItem} header={header} />;
      break;
    case !!componentDefinition:
      cellDefinition = (
        <RenderedCell
          host={host}
          header={header}
          dataItem={dataItem}
          componentDefinition={componentDefinition}
        />
      );
      break;
    case !!props.header.cellTemplate:
      cellDefinition = (
        <CellTemplate
          {...props}
          handleCellClick={() => handleCellClick(host, dataItem, header)}
        />
      );
      break;
    case !!editableCellConfig:
      cellDefinition = (
        <EditableCell
          editableCellConfig={editableCellConfig}
          dataItem={dataItem}
          header={header}
          theme={host.theme}
          hasTableActions={host.tableActions?.length > 0}
          mutatedValue={getMutatedValue(
            host._updatedRows,
            dataItem.rowId,
            header.dataPath
          )}
          handleCellClick={() => {
            handleCellClick(host, dataItem, header);
          }}
          disabledRow={disabledRow || isSubsetCell}
          disabledByCache={cellDisabledByCache(
            host,
            dataItem.rowId,
            header.dataPath
          )}
          addCellReference={(rowId, dataPath, ref) =>
            addCellReference(host, rowId, dataPath, ref)
          }
          updatedRows={host._updatedRows}
          handleCellUpdate={(rowId, dataPath, state, cleared) =>
            handleCellUpdate(host, rowId, dataPath, state, cleared)
          }
          identifier={identifier}
        />
      );
      break;

    case !!props.header.actionColumnConfig:
      cellDefinition = (
        <TableActionCell
          identifier={identifier}
          dataItem={dataItem}
          disabled={host._tableDisabled || disabledRow}
          header={header}
          buttonConfig={props.header.actionColumnConfig.buttonConfig}
          actionClicked={host.actionClicked}
          handleCellClick={() => {
            handleCellClick(host, dataItem, header);
          }}
          theme={host.theme}
        />
      );
      break;
    default:
      cellDefinition = <DefaultCell dataItem={dataItem} header={header} />;
  }
  return host.columnAdjustments?.[header.dataPath] ? (
    <RestrictedSizeCell size={host.columnAdjustments?.[header.dataPath]}>
      {cellDefinition}
    </RestrictedSizeCell>
  ) : (
    cellDefinition
  );
};

/**
 * In order to restrict column sizes to a specific size, we must wrap the content in a block element,
 * so that width properties are respected.
 * @param props
 * @param children
 * @constructor
 */
export const RestrictedSizeCell = (props: { size: string }, children) => {
  const { size } = props;

  return (
    <div
      class='restricted-size'
      style={{
        width: size,
        'min-width': size,
        'max-width': size,
      }}>
      {children}
    </div>
  );
};
